export type GraphQLEnv = Record<string, string | undefined>

export interface BrowserLocation {
  hostname: string
  origin: string
}

export function getDefaultEnv(): GraphQLEnv {
  if (typeof process !== 'undefined' && process.env) {
    return process.env as GraphQLEnv
  }
  return {}
}

function mergeBuildEnv(overrides?: GraphQLEnv): GraphQLEnv {
  return {
    ...getDefaultEnv(),
    // Bare `process.env.*` reads are statically replaced by Vite's EnvironmentPlugin (and
    // similar Next.js defines). Object-style access via getDefaultEnv() is not, which breaks
    // verify-boot after it deletes globalThis.process.
    GRAPHQL_URL_SELF_HOSTED: process.env.GRAPHQL_URL_SELF_HOSTED,
    NEXT_PUBLIC_GRAPHQL_URL: process.env.NEXT_PUBLIC_GRAPHQL_URL,
    VITE_GRAPHQL_URL: process.env.VITE_GRAPHQL_URL,
    VITE_GRAPHQL_PROXY_BYPASS_HOST_SUFFIXES: process.env.VITE_GRAPHQL_PROXY_BYPASS_HOST_SUFFIXES,
    NEXT_PUBLIC_GRAPHQL_PROXY_BYPASS_HOST_SUFFIXES:
      process.env.NEXT_PUBLIC_GRAPHQL_PROXY_BYPASS_HOST_SUFFIXES,
    ...overrides,
  }
}

export function getConfiguredGraphQLUrl({ env }: { env?: GraphQLEnv } = {}) {
  const merged = mergeBuildEnv(env)
  return merged.GRAPHQL_URL_SELF_HOSTED || merged.NEXT_PUBLIC_GRAPHQL_URL || merged.VITE_GRAPHQL_URL
}

export function getServerGraphQLUrl({ env }: { env?: GraphQLEnv } = {}) {
  const value = getConfiguredGraphQLUrl({ env })

  if (!value) {
    throw new Error('GraphQL URL is not configured')
  }

  let url: URL
  try {
    url = new URL(value)
  } catch {
    throw new Error('GraphQL URL must be an absolute HTTP(S) URL')
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    throw new Error('GraphQL URL must be an absolute HTTP(S) URL')
  }

  return url.href
}

export function parseProxyBypassHostSuffixes(env?: GraphQLEnv): string[] {
  const merged = mergeBuildEnv(env)
  const combined = [
    merged.VITE_GRAPHQL_PROXY_BYPASS_HOST_SUFFIXES,
    merged.NEXT_PUBLIC_GRAPHQL_PROXY_BYPASS_HOST_SUFFIXES,
  ]
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .join(',')

  return combined
    .split(',')
    .map(entry => entry.trim().toLowerCase())
    .filter(Boolean)
}

export function getRegistrableDomain(hostname: string): string {
  return hostname.split('.').slice(-2).join('.')
}

function isLoopbackHostname(hostname: string) {
  return hostname === 'localhost' || hostname === '127.0.0.1'
}

function isHttpOrigin(origin: string) {
  return origin.startsWith('http://') || origin.startsWith('https://')
}

export function getBrowserLocation(): BrowserLocation | null {
  if (typeof window === 'undefined') {
    return null
  }

  return { hostname: window.location.hostname, origin: window.location.origin }
}

export function shouldProxyThroughOrigin({
  location,
  backendHostname,
  env,
}: {
  location: BrowserLocation | null
  backendHostname: string
  env?: GraphQLEnv
}) {
  // `app://studio` is the desktop bundle, not a website. Its hostname fails the
  // domain check below, which would send API calls at the local page.
  if (!location || isLoopbackHostname(location.hostname) || !isHttpOrigin(location.origin)) {
    return false
  }

  const mergedEnv = mergeBuildEnv(env)
  const shouldBypassProxy = parseProxyBypassHostSuffixes(mergedEnv).some(suffix =>
    location.hostname.toLowerCase().endsWith(suffix),
  )
  if (shouldBypassProxy) {
    return false
  }

  return getRegistrableDomain(location.hostname) !== getRegistrableDomain(backendHostname)
}

export function resolveGraphQLUrl({
  env,
  location = getBrowserLocation(),
}: {
  env?: GraphQLEnv
  location?: BrowserLocation | null
} = {}) {
  const mergedEnv = mergeBuildEnv(env)
  const configuredUrl = getServerGraphQLUrl({ env: mergedEnv })

  if (!location) {
    return configuredUrl
  }

  const graphqlHostname = new URL(configuredUrl).hostname

  return shouldProxyThroughOrigin({ location, backendHostname: graphqlHostname, env: mergedEnv })
    ? new URL('/query', location.origin).href
    : configuredUrl
}
